import { IconOtherClose } from '@doltech/icons/IconOtherClose';
import * as React from 'react';
import styled from 'styled-components';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { MotionDrawerProps } from '@doltech/ui/lib/figma/MotionDrawer/MotionDrawer.types';
import { TableOfContentProps } from '@doltech/ui/lib/figma/Layout/Landing/Content/TableOfContent/TableOfContentType';
import { tableOfContentFlatMap } from '@doltech/ui/lib/figma/Layout/Landing/Content/TableOfContent/utils';
import {
  useTableOfContentScrollTracking,
} from '@doltech/ui/lib/figma/Layout/Landing/Content/useTableOfContentScrollTracking.hook';
import { MotionDrawer } from '@doltech/ui/lib/figma/MotionDrawer/MotionDrawer';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import {
  TocContentLayout,
} from '@doltech/ui/lib/figma/Layout/Landing/Content/TableOfContent/TocContentLayout/TocContentLayout';
import { ContentItemGroupWithEditorGrammarLanding } from './ContentItemGroupWithEditorGrammarLanding';
import {
  ContentItemGroup,
} from '@doltech/ui/lib/figma/Layout/Landing/Content/TableOfContent/ContentItemGroup/ContentItemGroup';

const CLOSE_BUTTON_SIZE = 24;

const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${CLOSE_BUTTON_SIZE}px;
  height: ${CLOSE_BUTTON_SIZE}px;
  min-width: ${CLOSE_BUTTON_SIZE}px;
  border-radius: 50%;
  cursor: pointer;
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border-bottom: 1px solid ${colorsV2.gray10};

  .toc-title {
    flex: 1;
    margin-right: 12px;
  }
`;

const ContentListWrapper = styled.div`
  padding: 16px 16px 32px;
  overflow: auto;
  .dol-editor {
    .editor-content-wrapper {
      .editor-editable-plugins {
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }
  }
`;

const GrammarTOCDrawerWrapper = styled.nav`
  display: flex;
  flex-direction: column;
  background-color: ${colorsV2.white100};
  max-height: 80vh;
  border-radius: 24px 24px 0 0;
`;

export interface TOCDrawerProps
  extends Omit<MotionDrawerProps, 'placement'>,
    Pick<TableOfContentProps, 'tocTitle' | 'items' | 'editorItems' | 'scrollContainerElm'> {
}

export const GrammarTOCDrawer = (props: TOCDrawerProps) => {
  const { tocTitle, items, editorItems, scrollContainerElm, onClose, ...rest } = props;

  const itemToCheck = items?.length ? items : editorItems;
  const sectionIds = React.useMemo(() => {
    return tableOfContentFlatMap(itemToCheck).map((item) => item.id);
  }, [itemToCheck]);
  const { currentSectionId } = useTableOfContentScrollTracking({
    sectionIds,
    scrollContainerElm,
    offsetY: 0,
  });

  return (
    <MotionDrawer placement="bottom" onClose={onClose} transparent {...rest}>
      <GrammarTOCDrawerWrapper>
        <Header className="toc-header">
          <ResponsiveTypography.Paragraph className="toc-title" variant="semi-bold/16">
            {tocTitle}
          </ResponsiveTypography.Paragraph>
          <CloseButton onClick={onClose}>
            <IconOtherClose fontSize={20}/>
          </CloseButton>
        </Header>

        <ContentListWrapper className="toc-content">
          <TocContentLayout>
            {editorItems?.map((item) => (
              <ContentItemGroupWithEditorGrammarLanding
                key={item.id}
                onClick={onClose}
                currentSectionId={currentSectionId}
                scrollContainerElm={scrollContainerElm}
                {...item}
              />
            ))}
            {items?.map((item) => (
              <ContentItemGroup
                key={item.id}
                currentSectionId={currentSectionId}
                onClick={onClose}
                scrollContainerElm={scrollContainerElm}
                {...item}
              />
            ))}
          </TocContentLayout>
        </ContentListWrapper>
      </GrammarTOCDrawerWrapper>
    </MotionDrawer>
  );
};
